import { Injectable } from '@angular/core';
import { MTMCustomFile, TransferType } from './mtm-custom-file';
import { UploadRequest, SignedURLUploadRequest } from './file.uploader';
import { TransferStatus } from './transfer-status';
import { TransferProgress } from './transfer-progress';
import { Subject } from 'rxjs';
import { listenerFileUploadCompleted, listenerUploadStatusChanged } from './listeners';
import { UploadHandlerFactory } from './upload-handlers/upload-handler-factory';
import { BaseUploadHandler } from './upload-handlers/base-upload-handler';

export type UploadStepHandler = (file: MTMCustomFile) => void;

@Injectable({
  providedIn: 'root',
})
export class SignedUrlUploadPipelineManager {
  constructor(private readonly uploadHandlerFactory: UploadHandlerFactory) {}

  /**
   * Sets up upload pipeline steps based on transfer type (Reference, Chunked, or Direct)
   * @param request - The upload request to construct pipelines for
   */
  public constructUploadPipelines(request: UploadRequest): void {
    request.files.forEach((item: MTMCustomFile) => {
      switch (request.uploadType) {
        case TransferType.Chunked:
          item.pipelines = ['preUpload', 'chunkUpload', 'postUpload', 'scanning', 'triggerNotification'];
          break;

        default:
          item.pipelines = ['preUpload', 'upload', 'postUpload', 'scanning', 'triggerNotification'];
          break;
      }

      const signedURLRequest: SignedURLUploadRequest = request as SignedURLUploadRequest;
      if (signedURLRequest.postUploadUrl) {
        item.postUploadUrl = signedURLRequest.postUploadUrl;
      }
      if (signedURLRequest.parentId) {
        item.parentId = signedURLRequest.parentId;
      }

      if (item.pipelines.length > 0) {
        const [firstStep] = item.pipelines;
        item.step = firstStep;
        listenerUploadStatusChanged.next(item);
      }
    });
  }

  /**
   * Advances file to next step in upload pipeline
   * @param mtmFile - The file to advance to next step
   */
  public proceedNextUploadStep(mtmFile: MTMCustomFile): void {
    let currentIndex: number = mtmFile.pipelines.indexOf(mtmFile.step);
    if (currentIndex > -1 && currentIndex < mtmFile.pipelines.length - 1) {
      currentIndex++;
      mtmFile.step = mtmFile.pipelines[currentIndex];
      listenerUploadStatusChanged.next(mtmFile);
      return;
    }

    mtmFile.step = 'done';
    listenerUploadStatusChanged.next(mtmFile);
    listenerFileUploadCompleted.emit(mtmFile);
    if (mtmFile.cleanUp) {
      mtmFile.cleanUp();
    } else {
      mtmFile.progress$.complete();
      mtmFile.progress$ = null;
    }
  }

  private handleStep(mtmFile: MTMCustomFile, stepHandlers: Map<string, UploadStepHandler>): void {
    let handler: UploadStepHandler | undefined;
    let uploadHandler: BaseUploadHandler | undefined;
    switch (mtmFile.step) {
      case 'postUpload':
        handler = stepHandlers.get('postUpload');
        if (handler) {
          handler(mtmFile);
        }
        break;

      case 'upload':
      case 'chunkUpload':
        uploadHandler = this.uploadHandlerFactory.getHandlerByStep(mtmFile.step);
        if (uploadHandler) {
          uploadHandler.handleUpload(mtmFile);
        }
        break;

      case 'triggerNotification':
        handler = stepHandlers.get('triggerNotification');
        if (handler) {
          handler(mtmFile);
        }
        break;

      case 'scanning':
        handler = stepHandlers.get('scanning');
        if (handler) {
          handler(mtmFile);
        }
        break;
    }
  }

  /**
   * Handles the current upload step for a file
   * @param mtmFile - The file to handle upload step for
   * @param stepHandlers - Map of step names to handler functions
   */
  public handleUploadStep(mtmFile: MTMCustomFile, stepHandlers: Map<string, UploadStepHandler>): void {
    if (!mtmFile.progress$) {
      mtmFile.progress$ = new Subject<TransferProgress>();
    }

    if (!navigator.onLine) {
      mtmFile.uploadStatusCode = TransferStatus.ERROR;
      listenerUploadStatusChanged.next(mtmFile);
      return;
    }

    const notificationHandler: UploadStepHandler | undefined = stepHandlers.get('triggerNotification');
    mtmFile.progress$.subscribe({
      next: (progress: TransferProgress) => {
        mtmFile.startIndex = progress.startIndex;

        switch (progress.step) {
          case 'next':
            this.proceedNextUploadStep(mtmFile);
            break;
          case 'error':
            //if the user is offline, set the upload status to error

            mtmFile.retryCount++;
            if (mtmFile.retryCount >= mtmFile.maxRetry) {
              mtmFile.uploadStatusCode = TransferStatus.ERROR;
              if (notificationHandler) {
                notificationHandler(mtmFile);
              }
              listenerUploadStatusChanged.next(mtmFile);
              return;
            }
            break;
          case 'cancel':
            mtmFile.uploadStatusCode = TransferStatus.CANCELED;
            listenerUploadStatusChanged.next(mtmFile);
            return;
          default:
            if (progress.step) {
              mtmFile.step = progress.step;
            }
            break;
        }

        this.handleStep(mtmFile, stepHandlers);
      },
      error: () => {
        mtmFile.uploadStatusCode = TransferStatus.ERROR;
      },
    });
  }
}
